/**
 * signatureOrder — „кой е ТЕКУЩИЯТ подпис на едно възлагане".
 *
 * Дотук въпросът не стоеше: едно възлагане имаше най-много един подпис, защото
 * `declined` беше терминален и второ подписване се отказваше с 412. Щом админът
 * може да ВЪРНЕ декларация за повторно попълване, възлагането получава втори
 * подпис — а старият НЕ се трие, защото е одитна следа (GDPR: доказателството
 * какво точно е отговорил родителят и кога).
 *
 * 🔴 Оттам дефектът: четците вземаха `limit(1)` БЕЗ подредба. Firestore не
 * обещава ред при заявка без `orderBy`, тоест екранът може да покаже ОТКАЗА
 * вместо новия подпис — недетерминирано, различно при всяко зареждане.
 *
 * Поправката е подредба по време, не изтриване. Живее ТУК, а не в четците, за
 * да не се разминат: `AssignmentsPage` (родителският екран) и `generatePDF`
 * (свалянето) трябва да сочат СЪЩИЯ документ — иначе екранът показва едно, а
 * PDF-ът сваля друго.
 *
 * ⚠️ Умишлено БЕЗ `orderBy` в самата заявка: тя вече филтрира по `assignmentId`
 * И `parentUserId`, тоест добавен `orderBy` иска НОВ композитен индекс. Липсващ
 * индекс гърми в момента на заявката (`failed-precondition`), а изграждането му
 * е асинхронно — тоест deploy-ът би счупил живия екран за неопределено време.
 * Подписите на едно възлагане са единици; подредбата е в паметта.
 */

import { toMillisOrNull } from './millis';

/** Полетата на подписа, които участват в подредбата. */
export interface SignatureOrderFields {
  signatureId?: string;
  /** Кога родителят е отговорил — основният ключ. */
  responseAt?: unknown;
  /** Резерва за заварени документи без `responseAt`. */
  metadata?: { createdAt?: unknown } | null;
}

/**
 * Времето на подписа в милисекунди. `0` при документ без нито едно от двете
 * полета — такъв губи от всеки датиран, което е верният ред: недатираният е
 * заварен, датираният е новият.
 */
export function signatureResponseMillis(sig: SignatureOrderFields | null | undefined): number {
  if (!sig) return 0;
  return toMillisOrNull(sig.responseAt) ?? toMillisOrNull(sig.metadata?.createdAt) ?? 0;
}

/**
 * Най-новият подпис от списъка, или `undefined` при празен списък.
 *
 * При равно време печели по-големият `signatureId` — равенството е рядко, но
 * недетерминиран изход при равенство е точно дефектът, който се поправя.
 */
export function pickNewestSignature<T extends SignatureOrderFields>(
  signatures: T[],
): T | undefined {
  let best: T | undefined;
  let bestMillis = -1;
  for (const sig of signatures) {
    if (!sig) continue;
    const millis = signatureResponseMillis(sig);
    if (
      best === undefined ||
      millis > bestMillis ||
      (millis === bestMillis && (sig.signatureId || '') > (best.signatureId || ''))
    ) {
      best = sig;
      bestMillis = millis;
    }
  }
  return best;
}
